import lodashCompact from 'lodash/fp/compact';
import typeCheck from 'common/util/typeCheck';

import {
  getListingApplicationFormUrl,
  findHouseholdApplication,
} from 'common/householdApplication/applicationHelpers';
import {
  getRentalApplicationUrl,
} from 'common/householdApplication/rentalApplicationHelpers';

import log from 'client/lib/log';
import getTenantScreeningCriteriaAction from 'client/actions/TenantScreeningCriteria/getTenantScreeningCriteria';
import getListingByIdAction from 'client/actions/Listing/getById';
import loadRenterProfileAction from 'client/actions/Household/loadRenterProfile';
import loadHouseholdRentersAction from 'client/actions/Household/loadHouseholdRenters';
import submitHouseholdApplicationAction from 'client/actions/Household/submitHouseholdApplication';
import getApplicationFormFields, {
  APPLICATION_FORM_TYPE_RENTER_PROFILE,
  APPLICATION_FORM_TYPE_GUARANTOR_APPLICATION,
} from 'common/householdApplication/applicationFormFields';
import {
  getNextPendingRequiredFormField,
} from 'client/helpers/formikHelpers';
import {
  HOUSEHOLD_APPLICATION_CATEGORY_HOUSEHOLD_APPLICATION,
} from 'common/householdConstants';
import {
  LISTING_DATA_SOURCE_SUNROOM,
} from 'common/listingConstants';

const debug = log.debug('sunroom:Household:redirectToApplicationForm');

export default async function redirectToApplicationForm(
  { state, history, apiClient },
  {
    formType,
    listingId,
    householdId,
    prequalListingId,
    requestedShowing,
    shouldRestart,
    shouldRequestTourPrompt,
  } = {},
) {
  typeCheck('history::NonEmptyObject', history);
  typeCheck('formType::NonEmptyString', formType);
  typeCheck('listingId::Maybe PositiveNumber', listingId);
  typeCheck('householdId::Maybe PositiveNumber', householdId);
  typeCheck('prequalListingId::Maybe PositiveNumber', prequalListingId);
  typeCheck('requestedShowing::Maybe Object', requestedShowing);
  typeCheck('shouldRestart::Maybe Boolean', shouldRestart);
  typeCheck('shouldRequestTourPrompt::Maybe Boolean', shouldRequestTourPrompt);

  const currentUser = state.get(['currentUser']);
  const [renterProfile, householdRenters, listing] = await Promise.all(lodashCompact([
    loadRenterProfileAction({ state, apiClient }, {
      userId: currentUser.id,
    }),
    loadHouseholdRentersAction({ state, apiClient }, {
      userId: currentUser.id,
    }),
    (listingId || prequalListingId)
    && getListingByIdAction({ state, apiClient }, listingId || prequalListingId),
  ]));

  debug({
    formType,
    listingId,
    householdId,
    prequalListingId,
    requestedShowing,
    shouldRestart,
    shouldRequestTourPrompt,
    listing,
  });

  // [PD-1968] New rental application flow for Sunroom listings
  if (listing
    && listing.dataSource === LISTING_DATA_SOURCE_SUNROOM
    && listing.listingManagementCompany
    && (
      formType === APPLICATION_FORM_TYPE_RENTER_PROFILE
      || formType === APPLICATION_FORM_TYPE_GUARANTOR_APPLICATION
    )
  ) {
    const tenantScreeningCriteria = await getTenantScreeningCriteriaAction({ state, apiClient }, {
      managementCompanyName: listing.listingManagementCompany,
    });
    if (tenantScreeningCriteria) {
      history.push(getRentalApplicationUrl({ userId: currentUser.id, listingId }));
      return;
    }
  }

  const roommates = householdRenters
    ? householdRenters.filter(({ id }) => id !== currentUser.id)
    : [];

  typeCheck('roommates::Array', roommates);

  const applicationFormFields = getApplicationFormFields({
    formValues: renterProfile,
    roommates: roommates,
    formType: formType,
    listing: listing,
  });

  const {
    firstFormField,
    nextFormField,
    lastFormField,
  } = getNextPendingRequiredFormField({
    formFields: applicationFormFields,
    values: renterProfile,
  });

  debug({
    applicationFormFields,
    firstFormField,
    nextFormField,
    lastFormField,
  });

  const householdApplication = listingId && findHouseholdApplication({
    householdApplications: state.get(['householdApplications']),
    category: HOUSEHOLD_APPLICATION_CATEGORY_HOUSEHOLD_APPLICATION,
    listingId: listingId,
  });

  if (
    formType === APPLICATION_FORM_TYPE_RENTER_PROFILE
    && !nextFormField
    && listingId
    && (
      householdApplication && !householdApplication.stripeChargeId
    )
  ) {
    await submitHouseholdApplicationAction({ state, apiClient }, {
      userId: currentUser.id,
      listingId: listingId,
      householdApplication: renterProfile,
    });
    history.push(`/application/${currentUser.id}/${listingId}/pay-form`);
  }
  else {
    let formFieldId = (lastFormField || nextFormField || firstFormField).id;
    if (shouldRestart) {
      formFieldId = firstFormField.id;
    }
    history.push(getListingApplicationFormUrl({
      userId: currentUser.id,
      listingId: listingId,
      householdId: householdId,
      formFieldId: formFieldId,
      formType: formType,
      shouldRequestTourPrompt: shouldRequestTourPrompt,
      prequalListingId: prequalListingId,
      requestedShowing,
    }));
  }
}
